import EmptyContent from '@/commons/empty-content/EmptyContent';
import Loader from '@/commons/loader';
import { useCompanyCategory } from '@/hooks/useCompanyCategory';
import BookingTotalIncomes from '@/modules/cash-register/components/money-indicator';
import { translates } from '@/utils';
import { Grid, Stack } from '@mui/material';
import { CardDetailBar, DataFormat } from '../components/card-detail-bar';
import { CardDetailColumn } from '../components/card-detail-column';
import { CardDetailPie } from '../components/card-detail-pie';
import { useMonitor } from '../hooks/useMonitor';

interface Props {
  params?: { firstDate?: string; lastDate?: string; isOpen?: boolean };
}

export const GraphicsMonitor = ({params }: Props) => {
  const { isFoodCompany } = useCompanyCategory();
  const series = [
    { x: 2016, y: 111 },
    { x: 2017, y: 136 },
    { x: 2018, y: 76 },
    { x: 2019, y: 108 },
    { x: 2020, y: 74 },
    { x: 2021, y: 54 },
    { x: 2022, y: 57 },
    { x: 2023, y: 111 },
  ];

  const {
    data,
    data2,
    data3,
    data5,
    dataCategories,
    isLoading,
    isLoading2,
    isLoading3,
    isLoading4,
    isLoadingCategories,
    pieTaxes,
    indicatorMethodsAndDiscount,
    notFoodCompanyData,
  } = useMonitor({ params });

  if (!params) {
    return (
      <EmptyContent
        title={translates.noData}
        sx={{
          '& span.MuiBox-root': { height: 160 },
        }}
      />
    );
  }

  if (isLoading || isLoading2 || isLoading3 || isLoading4 || isLoadingCategories) {
    return <Loader fullScreen />;
  }

  return (
    <>
      {!data ? (
        <EmptyContent
          title={translates.noData}
          sx={{
            '& span.MuiBox-root': { height: 160 },
          }}
        />
      ) : (
        <>
          <Grid item xs={12}>
            <CardDetailBar
              data={
                isFoodCompany
                  ? indicatorMethodsAndDiscount?.data
                  : (notFoodCompanyData as DataFormat[])
              }
              total={indicatorMethodsAndDiscount?.total || 0}
            />
          </Grid>

          {data2?.length && (
            <Grid item xs={12} md={12}>
              <Stack justifyContent="center" alignItems="center">
                <CardDetailPie data={data2!} isCurrency title={translates.paymentMethod} />
              </Stack>
            </Grid>
          )}
          {data5 && data5?.quantity?.values?.length > 0 && (
            <Grid item xs={12} md={6}>
              <CardDetailPie
                data={data5?.sellProducts!}
                isCurrency
                title={translates.moneyByProduct}
              />
            </Grid>
          )}
          {data5 && data5?.sellProducts && (
            <Grid item xs={12} md={6}>
              <CardDetailColumn data={data5?.quantity!} title={translates.quantityProductsSell} />
            </Grid>
          )}

          {pieTaxes?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Stack justifyContent="center" alignItems="center">
                <CardDetailPie data={pieTaxes} isCurrency title={translates.indicatorsReport} />
              </Stack>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <CardDetailPie data={data!} isCurrency title={translates.moneyByLines} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack justifyContent="center" alignItems="center">
              <CardDetailColumn data={data3!} isCurrency title={translates.amountProductByLines} />
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <CardDetailPie
              data={dataCategories?.moneyByCategories!}
              isCurrency
              title={translates.moneyByCategories}
            />
          </Grid>
          {dataCategories?.amountProductsByCategories?.values?.length && (
            <Grid item xs={12} md={6}>
              <Stack justifyContent="center" alignItems="center">
                <CardDetailColumn
                  data={dataCategories?.amountProductsByCategories}
                  isCurrency
                  title={translates.amountProductByCategories}
                />
              </Stack>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <BookingTotalIncomes
              total={indicatorMethodsAndDiscount?.originalData?.discount?.discount_value ?? 0}
              title={translates.discountMoment}
              color="secondary"
              chart={{
                series,
              }}
            />
          </Grid>
          {isFoodCompany && (
            <Grid item xs={12} md={6}>
              <BookingTotalIncomes
                total={indicatorMethodsAndDiscount?.originalData?.tips?.tips ?? 0}
                title={translates.tipsMoment}
                chart={{
                  series,
                }}
              />
            </Grid>
          )}
        </>
      )}
    </>
  );
};
